import { useRef, useState } from 'react';
import { Download, FileText, Loader2 } from 'lucide-react';

import { cn } from '../lib/utils';
import { BackendAPI } from '../lib/api';
import { triggerEsxDownload } from '../lib/esxDownload';
import type { AdapterReport, EsxGenerationResult, ApiError } from '../types';

interface ImportPdfToEsxProps {
  /** Optional cap on matched items (bounds backend retrieval round-trips). */
  maxItems?: number;
  className?: string;
  /** Called after a successful conversion (Phase 3: page refreshes balance + shows feedback prompt). */
  onConverted?: (result: EsxGenerationResult) => void;
  /** Called on conversion failure; the error carries `.status` (402/413/403). */
  onError?: (err: ApiError) => void;
  /** Called when the user picks a new file — lets the parent clear stale banners
   *  (e.g. a prior 402/413 shown outside this component). */
  onReset?: () => void;
}

/**
 * Faithful PDF import → ESX download.
 *
 * Pick a carrier PDF estimate; the backend parses, extracts quantities,
 * matches codes, and returns an importable .ESX which downloads in the browser.
 * No scope enhancement — replicates the source estimate.
 */
export default function ImportPdfToEsx({ maxItems, className, onConverted, onError, onReset }: ImportPdfToEsxProps) {
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<{ filename: string; report: AdapterReport } | null>(null);
  const [error, setError] = useState<string | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  async function handleImport() {
    if (!file) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const res = await BackendAPI.generateEsxFromPdf(file, maxItems);
      triggerEsxDownload(res.filename, res.esx_b64);
      setResult({ filename: res.filename, report: res.report });
      onConverted?.(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      onError?.(err as ApiError);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className={cn('space-y-3', className)}>
      <div className="flex flex-wrap items-center gap-3">
        <input
          ref={inputRef}
          data-testid="pdf-input"
          type="file"
          accept=".pdf"
          onChange={(e) => {
            setFile(e.target.files?.[0] ?? null);
            setError(null);
            setResult(null);
            onReset?.();
          }}
          className="text-sm"
        />
        <button
          type="button"
          onClick={handleImport}
          disabled={!file || busy}
          className={cn(
            'inline-flex items-center gap-2 rounded-md border px-3 py-2 text-sm font-medium transition-colors',
            file && !busy
              ? 'border-primary bg-primary text-white hover:bg-primary/90'
              : 'cursor-not-allowed border-slate-200 bg-slate-100 text-slate-400'
          )}
        >
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
          Import &amp; Download ESX
        </button>
        {file && (
          <span className="inline-flex items-center gap-1 text-sm text-muted-foreground">
            <FileText className="h-4 w-4" />
            {file.name}
          </span>
        )}
      </div>

      {result && (
        <div className="rounded-md border border-green-200 bg-green-50 p-3 text-sm text-green-800">
          Exported <span className="font-medium">{result.filename}</span>
          {' — '}
          {result.report.manual_entry_required.length} item(s) need manual code entry,{' '}
          {result.report.codes_not_in_catalog.length} unverified code(s).
        </div>
      )}
      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-800">
          {error}
        </div>
      )}
    </div>
  );
}
